"use client";
import {
  Braces,
  Check,
  ChevronRight,
  GitFork,
  RefreshCw,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import {
  useMemo,
} from "react";
import {
  compareReports,
} from "../../../lib/comparison";
import {
  type ScanReport,
} from "../../../lib/types";
import {
  truncateMiddle,
} from "../../shared/format";
import {
  IconButton,
  StatusDot,
} from "../../shared/primitives";


export function CompareView({
  report,
  comparisonReport,
  onPick,
  onClear,
}: {
  report: ScanReport;
  comparisonReport: ScanReport | null;
  onPick: () => void;
  onClear: () => void;
}) {
  const comparison = useMemo(
    () =>
      comparisonReport ? compareReports(report, comparisonReport) : null,
    [comparisonReport, report],
  );
  if (!comparison || !comparisonReport) {
    return (
      <section className="compare-empty panel">
        <span className="compare-emblem">
          <RefreshCw size={31} />
        </span>
        <div className="section-kicker">VERSION DIFFERENTIAL</div>
        <h2>Compare against another JAR</h2>
        <p>
          Find added and removed files, changed classes, renamed methods,
          behavior drift, new actions, and new findings. Both files remain in
          this browser.
        </p>
        <button className="primary-button" type="button" onClick={onPick}>
          <Upload size={15} />
          Choose comparison JAR
        </button>
      </section>
    );
  }
  return (
    <div className="compare-workspace">
      <section className="compare-header panel">
        <div>
          <span>BASELINE</span>
          <strong>{report.fileName}</strong>
          <code>{truncateMiddle(report.hashes.sha256)}</code>
        </div>
        <span className="compare-score">
          <strong>{comparison.archiveSimilarity.toFixed(1)}%</strong>
          <small>behavior similarity</small>
        </span>
        <div>
          <span>COMPARISON</span>
          <strong>{comparisonReport.fileName}</strong>
          <code>{truncateMiddle(comparisonReport.hashes.sha256)}</code>
        </div>
        <button className="secondary-button compact" type="button" onClick={onPick}>
          <RefreshCw size={13} />
          Replace
        </button>
        <IconButton label="Clear comparison" onClick={onClear}>
          <X size={14} />
        </IconButton>
      </section>
      <section className="compare-stat-grid">
        <article>
          <span>SCORE DELTA</span>
          <strong className={comparison.scoreDelta > 0 ? "tone-critical" : ""}>
            {comparison.scoreDelta > 0 ? "+" : ""}
            {comparison.scoreDelta}
          </strong>
        </article>
        <article>
          <span>ADDED FILES</span>
          <strong>{comparison.addedEntries.length}</strong>
        </article>
        <article>
          <span>REMOVED FILES</span>
          <strong>{comparison.removedEntries.length}</strong>
        </article>
        <article>
          <span>CHANGED FILES</span>
          <strong>{comparison.changedEntries.length}</strong>
        </article>
        <article>
          <span>NEW ACTIONS</span>
          <strong>{comparison.addedActions.length}</strong>
        </article>
      </section>
      <section className="panel compare-summary">
        <ShieldCheck size={18} />
        <p>{comparison.summary}</p>
      </section>
      <div className="compare-grid">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">SEMANTIC DIFF</div>
              <h3>Changed and renamed classes</h3>
            </div>
          </div>
          <div className="class-diff-list">
            {comparison.likelyRenamedClasses.map((item) => (
              <div key={`${item.beforeClass}:${item.afterClass}`}>
                <GitFork size={15} />
                <span>
                  <code>{item.beforeClass}</code>
                  <ChevronRight size={12} />
                  <code>{item.afterClass}</code>
                </span>
                <strong>{item.similarity.toFixed(1)}%</strong>
              </div>
            ))}
            {comparison.changedClasses.map((item) => (
              <div key={item.beforeClass}>
                <Braces size={15} />
                <span>
                  <code>{item.beforeClass}</code>
                </span>
                <strong>{item.similarity.toFixed(1)}%</strong>
              </div>
            ))}
            {!comparison.changedClasses.length &&
              !comparison.likelyRenamedClasses.length && (
                <div className="empty-panel">
                  <Check size={22} />
                  <strong>No class behavior drift</strong>
                </div>
              )}
          </div>
        </section>
        <section className="panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">BEHAVIOR DRIFT</div>
              <h3>New actions and findings</h3>
            </div>
          </div>
          <div className="behavior-diff-list">
            {comparison.addedActions.map((item) => (
              <div key={`${item.id}:${item.entry}`}>
                <StatusDot severity={item.severity} />
                <span>
                  <strong>{item.title}</strong>
                  <code>{item.entry}</code>
                </span>
                <small>new action</small>
              </div>
            ))}
            {comparison.addedFindings.slice(0, 30).map((item) => (
              <div key={`${item.ruleId}:${item.entry}`}>
                <StatusDot severity={item.severity} />
                <span>
                  <strong>{item.title}</strong>
                  <code>{item.ruleId}</code>
                </span>
                <small>new finding</small>
              </div>
            ))}
            {!comparison.addedActions.length &&
              !comparison.addedFindings.length && (
                <div className="empty-panel">
                  <Check size={22} />
                  <strong>No new detected behavior</strong>
                </div>
              )}
          </div>
        </section>
      </div>
      <section className="panel entry-diff-panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">ARCHIVE INVENTORY</div>
            <h3>File changes</h3>
          </div>
        </div>
        <div className="entry-diff-columns">
          <div>
            <span>ADDED</span>
            {comparison.addedEntries.slice(0, 120).map((path) => (
              <code key={path}>+ {path}</code>
            ))}
          </div>
          <div>
            <span>REMOVED</span>
            {comparison.removedEntries.slice(0, 120).map((path) => (
              <code key={path}>− {path}</code>
            ))}
          </div>
          <div>
            <span>CHANGED</span>
            {comparison.changedEntries.slice(0, 120).map((entry) => (
              <code key={entry.path}>~ {entry.path}</code>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
